import React, { useState, useEffect } from 'react';
import { transactionAPI } from '../services/api';
import { useScrollLock } from '../hooks/useScrollLock';

interface Transaction {
  id: number;
  description: string;
  amount: number;
  date: string;
  account_name?: string;
  institution_name?: string;
  merchant_name?: string;
}

interface Category {
  id: number;
  name: string;
  color: string;
  type: 'income' | 'expense';
}

interface DeletionImpact {
  category: Category;
  impact: {
    transactions: Transaction[];
    transaction_count: number;
    rules: any[];
    rule_count: number;
    subcategories: any[];
    subcategory_count: number;
  };
  alternatives: Category[];
}

interface CategoryDeletionModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number;
  onDeleted: () => void;
}

export const CategoryDeletionModal: React.FC<CategoryDeletionModalProps> = ({
  isOpen,
  onClose,
  categoryId,
  onDeleted,
}) => {
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [impact, setImpact] = useState<DeletionImpact | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Prevent background scrolling when modal is open
  useScrollLock(isOpen);
  const [selectedTransactions, setSelectedTransactions] = useState<Set<number>>(new Set());
  const [reassignCategory, setReassignCategory] = useState<number | undefined>();
  const [reassignSubcategory, setReassignSubcategory] = useState<number | undefined>();
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [showBulkReassign, setShowBulkReassign] = useState(false);

  useEffect(() => {
    if (isOpen && categoryId) {
      loadDeletionImpact();
    }
  }, [isOpen, categoryId]);

  useEffect(() => {
    if (reassignCategory) {
      loadSubcategories(reassignCategory);
    } else {
      setSubcategories([]);
      setReassignSubcategory(undefined);
    }
  }, [reassignCategory]);

  const loadDeletionImpact = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await transactionAPI.getCategoryDeletionImpact(categoryId);
      setImpact(data);
      
      // Select all transactions by default for bulk operations
      if (data.impact.transactions.length > 0) {
        setSelectedTransactions(new Set(data.impact.transactions.map((t: Transaction) => t.id)));
      }
    } catch (err) {
      setError('Failed to load deletion impact');
      console.error('Error loading deletion impact:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubcategories = async (categoryId: number) => {
    try {
      const data = await transactionAPI.getSubcategories(categoryId);
      setSubcategories(data);
    } catch (err) {
      console.error('Error loading subcategories:', err);
    }
  };

  const handleTransactionSelect = (transactionId: number) => {
    const newSelected = new Set(selectedTransactions);
    if (newSelected.has(transactionId)) {
      newSelected.delete(transactionId);
    } else {
      newSelected.add(transactionId);
    }
    setSelectedTransactions(newSelected);
  };

  const handleSelectAll = () => {
    if (!impact) return;
    
    if (selectedTransactions.size === impact.impact.transactions.length) {
      setSelectedTransactions(new Set());
    } else {
      setSelectedTransactions(new Set(impact.impact.transactions.map((t: Transaction) => t.id)));
    }
  };

  const handleBulkReassign = async () => {
    if (!reassignCategory || selectedTransactions.size === 0) return;

    try {
      setDeleting(true);
      await transactionAPI.bulkReassignTransactions({
        transaction_ids: Array.from(selectedTransactions),
        new_category_id: reassignCategory,
        new_subcategory_id: reassignSubcategory,
      });

      // Reload impact to show updated state
      await loadDeletionImpact();
      setSelectedTransactions(new Set());
      setShowBulkReassign(false);
    } catch (err) {
      setError('Failed to reassign transactions');
      console.error('Error reassigning transactions:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!impact) return;

    try {
      setDeleting(true);
      
      // If there are still transactions and a reassign category is selected, 
      // reassign remaining transactions
      const reassignTo = reassignCategory ? {
        categoryId: reassignCategory,
        subcategoryId: reassignSubcategory,
      } : undefined;

      await transactionAPI.deleteCategory(categoryId, reassignTo);
      onDeleted();
      onClose();
    } catch (err) {
      setError('Failed to delete category');
      console.error('Error deleting category:', err);
    } finally {
      setDeleting(false);
    }
  };

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 text-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white">Delete Category</h2>
          <button 
            className="text-gray-400 hover:text-white transition-colors"
            onClick={onClose}
          >
            <span className="text-2xl">×</span>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <p className="mt-4 text-gray-300">Analyzing impact...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-900/50 border border-red-700 rounded-lg p-4 mb-6">
              <p className="text-red-200">{error}</p>
            </div>
          )}

          {impact && (
            <>
              <div className="flex items-center space-x-3 mb-6">
                <div 
                  className="w-6 h-6 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: impact.category.color }}
                ></div>
                <h3 className="text-lg font-semibold text-white">{impact.category.name}</h3>
                <span className="px-2 py-1 text-xs font-medium bg-gray-700 text-gray-300 rounded-full">
                  {impact.category.type}
                </span>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="text-md font-medium text-white mb-3">Deletion Impact</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-400">{impact.impact.transaction_count}</div>
                    <div className="text-sm text-gray-400">Transactions</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-400">{impact.impact.rule_count}</div>
                    <div className="text-sm text-gray-400">Rules</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-400">{impact.impact.subcategory_count}</div>
                    <div className="text-sm text-gray-400">Subcategories</div>
                  </div>
                </div>
              </div>

              {impact.impact.transaction_count > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-white">
                      Affected Transactions ({impact.impact.transaction_count})
                    </h4>
                    <div className="flex space-x-2">
                      <button 
                        className="px-3 py-1 text-sm bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white rounded transition-colors"
                        onClick={handleSelectAll}
                      >
                        {selectedTransactions.size === impact.impact.transactions.length 
                          ? 'Deselect All' 
                          : 'Select All'
                        }
                      </button>
                      {selectedTransactions.size > 0 && (
                        <button 
                          className="px-3 py-1 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
                          onClick={() => setShowBulkReassign(!showBulkReassign)}
                        >
                          Reassign {selectedTransactions.size} Selected
                        </button>
                      )}
                    </div>
                  </div>

                  {showBulkReassign && (
                    <div className="bg-gray-800 rounded-lg p-4 mb-4">
                      <h5 className="text-sm font-medium text-white mb-3">
                        Reassign {selectedTransactions.size} transactions to:
                      </h5>
                      <div className="space-y-3">
                        <select
                          value={reassignCategory || ''}
                          onChange={(e) => setReassignCategory(Number(e.target.value) || undefined)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select category...</option>
                          {impact.alternatives.map(cat => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name} ({cat.type})
                            </option>
                          ))}
                        </select>

                        {subcategories.length > 0 && (
                          <select
                            value={reassignSubcategory || ''}
                            onChange={(e) => setReassignSubcategory(Number(e.target.value) || undefined)}
                            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select subcategory...</option>
                            {subcategories.map(sub => (
                              <option key={sub.id} value={sub.id}>
                                {sub.name}
                              </option>
                            ))}
                          </select>
                        )}

                        <div className="flex space-x-2">
                          <button 
                            className="px-4 py-2 bg-green-600 text-white hover:bg-green-700 rounded transition-colors disabled:opacity-50"
                            onClick={handleBulkReassign}
                            disabled={!reassignCategory || deleting}
                          >
                            {deleting ? 'Reassigning...' : 'Reassign Selected'}
                          </button>
                          <button 
                            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded transition-colors"
                            onClick={() => setShowBulkReassign(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
                    <div className="max-h-80 overflow-y-auto">
                      {impact.impact.transactions.map(transaction => (
                        <div 
                          key={transaction.id} 
                          className={`flex items-start p-4 border-b border-gray-700 last:border-b-0 hover:bg-gray-750 transition-colors ${
                            selectedTransactions.has(transaction.id) ? 'bg-blue-900/30 border-l-4 border-l-blue-500' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedTransactions.has(transaction.id)}
                            onChange={() => handleTransactionSelect(transaction.id)}
                            className="mt-1 mr-3 h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {transaction.merchant_name || transaction.description}
                                </p>
                                <div className="mt-1 flex items-center space-x-4 text-sm text-gray-400">
                                  <span>{formatDate(transaction.date)}</span>
                                  {transaction.account_name && (
                                    <span className="truncate">
                                      {transaction.institution_name} - {transaction.account_name}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="ml-4 flex-shrink-0">
                                <p className={`text-sm font-semibold ${
                                  transaction.amount < 0 ? 'text-red-400' : 'text-green-400'
                                }`}>
                                  {formatAmount(transaction.amount)}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {impact.impact.rule_count > 0 && (
                <div className="bg-yellow-900/50 border border-yellow-700 rounded-lg p-4 mb-6">
                  <h4 className="text-yellow-200 font-medium mb-2">
                    ⚠️ {impact.impact.rule_count} categorization rules will be deleted
                  </h4>
                  <p className="text-yellow-300 text-sm">
                    These rules automatically categorize transactions. They will be permanently removed.
                  </p>
                </div>
              )}

              <div className="bg-gray-800 rounded-lg p-4 mb-6">
                <h4 className="text-white font-medium mb-4">Final Action for Remaining Transactions</h4>
                <div className="space-y-3">
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="finalAction"
                      value="uncategorize"
                      checked={!reassignCategory}
                      onChange={() => setReassignCategory(undefined)}
                      className="h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-300">Leave remaining transactions uncategorized</span>
                  </label>
                  <label className="flex items-center space-x-3">
                    <input
                      type="radio"
                      name="finalAction"
                      value="reassign"
                      checked={!!reassignCategory}
                      onChange={() => setReassignCategory(impact.alternatives[0]?.id)}
                      className="h-4 w-4 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                    />
                    <span className="text-gray-300">Reassign remaining transactions to:</span>
                  </label>
                  {reassignCategory && (
                    <div className="ml-7 space-y-2">
                      <select
                        value={reassignCategory || ''}
                        onChange={(e) => setReassignCategory(Number(e.target.value) || undefined)}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        {impact.alternatives.map(cat => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name} ({cat.type})
                          </option>
                        ))}
                      </select>
                      {subcategories.length > 0 && (
                        <select
                          value={reassignSubcategory || ''}
                          onChange={(e) => setReassignSubcategory(Number(e.target.value) || undefined)}
                          className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="">Select subcategory...</option>
                          {subcategories.map(sub => (
                            <option key={sub.id} value={sub.id}>
                              {sub.name}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-700 bg-gray-800">
          <button 
            className="px-4 py-2 bg-gray-600 text-white hover:bg-gray-700 rounded transition-colors"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded transition-colors disabled:opacity-50"
            onClick={handleDeleteCategory}
            disabled={deleting || loading}
          >
            {deleting ? 'Deleting...' : 'Delete Category'}
          </button>
        </div>
      </div>
    </div>
  );
};
